import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { SectionTitle, Btn, Modal, Field, TextArea, EmptyState, Badge } from "@/components/Brutal";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Building2 } from "lucide-react";

const empty = { nome: "", tipo_pessoa: "PJ", cnpj: "", cpf: "", razao_social: "", telefone: "", email: "", contato: "", endereco: "", cidade: "", uf: "", categoria: "", observacoes: "" };

const maskCNPJ = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

const maskCPF = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d{1,2})$/, ".$1-$2");
};

export default function Suppliers() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("almoxarife");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => { setLoading(true); const { data } = await api.get("/suppliers"); setItems(data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      // Limpa o documento não usado conforme o tipo de pessoa
      const payload = { ...form };
      if (payload.tipo_pessoa === "PJ") payload.cpf = "";
      else payload.cnpj = "";
      if (editing) await api.put(`/suppliers/${editing.id}`, payload);
      else await api.post("/suppliers", payload);
      toast.success(editing ? "Fornecedor atualizado" : "Fornecedor cadastrado");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!confirm("Excluir fornecedor?")) return; try { await api.delete(`/suppliers/${id}`); toast.success("Excluído"); load(); } catch (err) { toast.error(formatError(err.response?.data?.detail)); } };

  const docOf = (s) => {
    if ((s.tipo_pessoa || "PJ") === "PF") return s.cpf || "";
    return s.cnpj || "";
  };
  const docLabel = (s) => ((s.tipo_pessoa || "PJ") === "PF" ? "CPF" : "CNPJ");

  const filtered = items.filter((s) => {
    const t = q.toLowerCase();
    const doc = docOf(s);
    return !t || s.nome.toLowerCase().includes(t) || doc.includes(t) || (s.cidade || "").toLowerCase().includes(t) || (s.categoria || "").toLowerCase().includes(t);
  });

  const startEdit = (s) => {
    setEditing(s);
    setForm({
      ...empty,
      ...s,
      tipo_pessoa: s.tipo_pessoa || (s.cpf && !s.cnpj ? "PF" : "PJ"),
    });
    setOpen(true);
  };

  return (
    <div data-testid="suppliers-page">
      <SectionTitle action={canEdit && <Btn onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="add-supplier-btn"><Plus className="w-4 h-4" />Novo Fornecedor</Btn>}>
        Fornecedores · <Building2 className="inline w-5 h-5 -mt-1" />
      </SectionTitle>

      <div className="brutal-card-sm flex items-center gap-2 px-3 py-2 mb-4 bg-white max-w-md">
        <Search className="w-4 h-4 text-[#555]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, CNPJ/CPF, cidade..." className="flex-1 outline-none text-sm bg-transparent" data-testid="suppliers-search" />
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead><tr><th>Nome</th><th>Tipo</th><th>CNPJ / CPF</th><th>Categoria</th><th>Contato</th><th>Cidade/UF</th><th>Telefone</th>{canEdit && <th className="w-24">Ações</th>}</tr></thead>
          <tbody>
            {loading && <tr><td colSpan={canEdit ? 8 : 7} className="text-center py-8 text-[#777] font-mono-data">Carregando...</td></tr>}
            {!loading && filtered.length === 0 && <EmptyState message="Nenhum fornecedor." cols={canEdit ? 8 : 7} />}
            {filtered.map((s) => (
              <tr key={s.id} data-testid={`supplier-row-${s.id}`}>
                <td>
                  <div className="font-medium">{s.nome}</div>
                  {s.razao_social && <div className="text-xs text-[#777]">{s.razao_social}</div>}
                </td>
                <td><Badge variant={(s.tipo_pessoa || "PJ") === "PF" ? "warn" : "green"}>{docLabel(s)}</Badge></td>
                <td className="font-mono-data text-xs">{docOf(s) || "—"}</td>
                <td>{s.categoria ? <Badge variant="green">{s.categoria}</Badge> : "—"}</td>
                <td className="text-sm">{s.contato || "—"}</td>
                <td className="text-sm">{[s.cidade, s.uf].filter(Boolean).join(" / ") || "—"}</td>
                <td className="font-mono-data text-xs">{s.telefone || "—"}</td>
                {canEdit && <td><div className="flex gap-1">
                  <button onClick={() => startEdit(s)} data-testid={`edit-supplier-${s.id}`} className="p-1.5 hover:bg-[#FF4F00] hover:text-white border border-[#111]"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(s.id)} data-testid={`delete-supplier-${s.id}`} className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]"><Trash2 className="w-3.5 h-3.5" /></button>
                </div></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar Fornecedor" : "Novo Fornecedor"} size="xl">
        <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="supplier-form">
          <Field label="Nome / Fantasia *" required value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} colspan />

          <div className="col-span-2">
            <label className="block text-xs font-bold uppercase tracking-wider text-[#111] mb-1">Tipo de Pessoa *</label>
            <div className="flex gap-2" data-testid="supplier-tipo-pessoa">
              <button
                type="button"
                onClick={() => setForm({ ...form, tipo_pessoa: "PJ" })}
                data-testid="tipo-pessoa-pj"
                className={`px-4 py-2 border-2 border-[#111] text-sm font-bold transition ${form.tipo_pessoa === "PJ" ? "bg-[#111] text-white" : "bg-white text-[#111] hover:bg-[#f3f3f3]"}`}
              >
                Pessoa Jurídica (CNPJ)
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, tipo_pessoa: "PF" })}
                data-testid="tipo-pessoa-pf"
                className={`px-4 py-2 border-2 border-[#111] text-sm font-bold transition ${form.tipo_pessoa === "PF" ? "bg-[#111] text-white" : "bg-white text-[#111] hover:bg-[#f3f3f3]"}`}
              >
                Pessoa Física (CPF)
              </button>
            </div>
          </div>

          {form.tipo_pessoa === "PJ" ? (
            <>
              <Field label="CNPJ" value={form.cnpj} onChange={(v) => setForm({ ...form, cnpj: maskCNPJ(v) })} placeholder="00.000.000/0000-00" testid="supplier-cnpj" />
              <Field label="Razão Social" value={form.razao_social} onChange={(v) => setForm({ ...form, razao_social: v })} />
            </>
          ) : (
            <>
              <Field label="CPF" value={form.cpf} onChange={(v) => setForm({ ...form, cpf: maskCPF(v) })} placeholder="000.000.000-00" testid="supplier-cpf" />
              <Field label="Nome Completo (opcional)" value={form.razao_social} onChange={(v) => setForm({ ...form, razao_social: v })} />
            </>
          )}

          <Field label="Categoria" value={form.categoria} onChange={(v) => setForm({ ...form, categoria: v })} placeholder="Peças · Alimentos · EPIs · Combustível..." />
          <Field label="Pessoa de Contato" value={form.contato} onChange={(v) => setForm({ ...form, contato: v })} />
          <Field label="Telefone" value={form.telefone} onChange={(v) => setForm({ ...form, telefone: v })} />
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} colspan />
          <Field label="Endereço" value={form.endereco} onChange={(v) => setForm({ ...form, endereco: v })} colspan />
          <Field label="Cidade" value={form.cidade} onChange={(v) => setForm({ ...form, cidade: v })} />
          <Field label="UF" value={form.uf} onChange={(v) => setForm({ ...form, uf: v })} />
          <TextArea label="Observações" value={form.observacoes} onChange={(v) => setForm({ ...form, observacoes: v })} />
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-supplier-btn">{editing ? "Salvar" : "Criar"}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
